import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { mockData } from '../data/mock';
import { Button } from './ui/button';
import { ArrowRight, CheckCircle, ClipboardCheck } from 'lucide-react';
import QuizModal from './QuizModal';

const HeroSection = () => {
  const { language } = useLanguage();
  const [quizOpen, setQuizOpen] = useState(false);
  const hero = mockData.hero[language];

  return (
    <section id="home" className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-gradient-to-br from-slate-50 via-teal-50/30 to-emerald-50/20">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-teal-200/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-200/20 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 py-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="inline-block">
              <span className="px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold">
                {hero.tagline}
              </span>
            </div>

            <div>
              <p className="text-5xl lg:text-6xl font-bold text-gray-900 mb-4 leading-tight">
                {hero.title}
              </p>
              <h1 className="text-6xl lg:text-7xl font-bold bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent mb-6">
                {hero.subtitle}
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                {hero.description}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg"
                onClick={() => setQuizOpen(true)}
                className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white text-lg px-8 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group"
              >
                <ClipboardCheck className={`w-5 h-5 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
                {language === 'en' ? '🎯 Free Level Test' : '🎯 اختبار المستوى المجاني'}
              </Button>
              <Button 
                size="lg" 
                className="bg-teal-600 hover:bg-teal-700 text-white text-lg px-8 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group"
              >
                {hero.cta}
                <ArrowRight className={`w-5 h-5 ${language === 'ar' ? 'mr-2' : 'ml-2'} group-hover:translate-x-1 transition-transform`} />
              </Button>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-8">
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">{language === 'en' ? 'All Ages & Levels' : 'جميع الأعمار والمستويات'}</h4>
                  <p className="text-sm text-gray-600">{language === 'en' ? 'Courses for everyone' : 'دورات للجميع'}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">{language === 'en' ? 'Native Teachers' : 'معلمون أصليون'}</h4>
                  <p className="text-sm text-gray-600">{language === 'en' ? 'From Canada, USA & UK' : 'من كندا وأمريكا وبريطانيا'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Image */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_canada-arabic/artifacts/jzqvs5xv_67c0df464234b9a6d90702ab.png"
                alt="Canada English Life Academy Team"
                className="w-full h-[600px] object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 to-transparent"></div>
            </div>

            {/* Floating Card */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6 max-w-xs">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl">🎓</span>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">5000+</p>
                  <p className="text-sm text-gray-600">{language === 'en' ? 'Happy Students' : 'طالب سعيد'}</p>
                </div>
              </div>
            </div>

            {/* Registration Badge */}
            <div className="absolute -top-6 -right-6 bg-white rounded-2xl shadow-xl p-6">
              <img
                src="https://images.unsplash.com/photo-1579546929518-9e396f3cc809?w=200&q=80"
                alt="Official Registration"
                className="w-32 h-32 object-contain"
                loading="lazy"
              />
              <p className="text-center text-xs font-semibold text-gray-700 mt-2">
                {language === 'en' ? 'Official Registration' : 'تسجيل رسمي'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quiz Modal */}
      <QuizModal isOpen={quizOpen} onClose={() => setQuizOpen(false)} />
    </section>
  );
};

export default HeroSection;
